import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileSearch, Inbox } from 'lucide-react';
import { ErrorState } from '../AsyncStates';
import { Skeleton } from '../ui/Skeleton';
import { cn } from '../../utils/cn';

/**
 * The four things a report screen shows when it is not showing figures.
 *
 * They are separate components rather than one `state` prop because they carry different content:
 * idle teaches the control, loading mimics the table it is about to become, empty ECHOES the filter
 * that produced nothing (which is the answer most of the time), and error offers a retry.
 */

interface PanelProps {
  children: React.ReactNode;
  className?: string;
  testId?: string;
}

function Panel({ children, className, testId }: PanelProps) {
  return (
    <div
      data-testid={testId}
      className={cn(
        'flex flex-col items-center gap-2 rounded-lg border border-border bg-card px-6 py-12 text-center',
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface ReportIdleProps {
  /** Overrides the default "Choose a period, then run the report" line. */
  hint?: string;
  className?: string;
  testId?: string;
}

export function ReportIdle({ hint, className, testId }: ReportIdleProps) {
  const { t } = useTranslation();
  return (
    <Panel className={className} testId={testId ?? 'report-idle'}>
      <FileSearch className="h-10 w-10 text-muted-foreground/60" aria-hidden="true" />
      <p className="text-sm text-muted-foreground">{hint ?? t('reports.idleHint')}</p>
    </Panel>
  );
}

export interface ReportLoadingProps {
  /** Number of skeleton columns. Match the report so the shape does not jump on arrival. */
  columns?: number;
  rows?: number;
  className?: string;
  testId?: string;
}

export function ReportLoading({
  columns = 6,
  rows = 8,
  className,
  testId,
}: ReportLoadingProps) {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={t('reports.loading')}
      data-testid={testId ?? 'report-loading'}
      className={cn('overflow-hidden rounded-lg border border-border bg-card', className)}
    >
      <div className="flex gap-3 border-b border-border bg-muted px-3 py-3">
        {Array.from({ length: columns }).map((_, index) => (
          <Skeleton key={`head-${index}`} className="h-3 flex-1" />
        ))}
      </div>
      <div className="divide-y divide-border">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={`row-${rowIndex}`} className="flex gap-3 px-3 py-3">
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton key={`cell-${rowIndex}-${colIndex}`} className="h-4 flex-1" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export interface ReportEmptyProps {
  /** Localized period the report ran for, e.g. `1–31 Aug 2026`. */
  period?: string;
  /** Localized scope, e.g. `All departments`. */
  scopeText?: string;
  className?: string;
  testId?: string;
}

export function ReportEmpty({ period, scopeText, className, testId }: ReportEmptyProps) {
  const { t } = useTranslation();
  const filters = [period, scopeText].filter(Boolean).join(' · ');
  return (
    <Panel className={className} testId={testId ?? 'report-empty'}>
      <Inbox className="h-10 w-10 text-muted-foreground/60" aria-hidden="true" />
      <p className="text-sm font-medium text-foreground">{t('reports.emptyTitle')}</p>
      {filters && <p className="text-xs text-muted-foreground">{filters}</p>}
      <p className="max-w-[52ch] text-xs text-muted-foreground">{t('reports.emptyHint')}</p>
    </Panel>
  );
}

export interface ReportErrorProps {
  message: string;
  onRetry?: () => void;
  className?: string;
  testId?: string;
}

export function ReportError({ message, onRetry, className, testId }: ReportErrorProps) {
  const { t } = useTranslation();
  return (
    <div
      data-testid={testId ?? 'report-error'}
      className={cn('rounded-lg border border-border bg-card px-6 py-12', className)}
    >
      <ErrorState
        title={t('reports.errorTitle')}
        message={message}
        onRetry={onRetry}
        retryText={t('reports.retry')}
        size="sm"
      />
    </div>
  );
}
